import React, { useState, FC, useEffect } from 'react'
import Modal from '@bt-react/components/Modal'
import Button from '@bt-react/components/Button'
import Countdown from '@bt-react/components/Countdown'
import SystemCheck from '../SystemCheck'
import Icon, { IconType } from '@bt-react/components/Icon'
import { PRESENTER, OBSERVER } from '../../dataStore/constants/roles'
import componentStyles from './AccessModalHorizontal.module.scss'
import useIsTalkLive from '../../lib/useIsTalkLive'
import { AccessModalHorizontalProps } from './types'
import audit from '../../lib/audit/audit'
import apiGateway from '../../services/apiGateway'
import getEnvironment from '@bt-react/libs/getEnvironment'

const AccessModal: FC<AccessModalHorizontalProps> = props => {

  const {
    setRole = () => {},
    isDisabled = false,
    joinRoom = () => {},
    roomname = ''
  } = props

  const isLive = useIsTalkLive(props.date)
  const [ isPresenterLoading, setIsPresenterLoading ] = useState(false)
  const [ isObserverLoading, setIsObserverLoading ] = useState(false)
  const [ isAdmin, setIsAdmin ] = useState(false)

  const checkIsAdmin = async () => {
    if (getEnvironment().domain === 'localhost') {
      setIsAdmin(true)
      return
    }

    try {
      await apiGateway.getCheckAdminRights()
      setIsAdmin(true)
    } catch (e) {
      console.log('Error checking admin rights: ', e)
      setIsAdmin(false)
    }
  }

  useEffect(() => {
    audit.addEntry('PresenterService', 'AccessModalDisplayed', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      }
    }))
    checkIsAdmin()
  }, [])

  if (!props.date) return null

  return <Modal
    concise={true}
    isOpen={true}
    hideBorder={true}
  >
    <Modal.Header
      className={componentStyles[`Header`]}
      dataBdd='dialog-access-header'
      hideBorder={true}
    >
      <SystemCheck className={componentStyles['quality-checker']} />
      {
        isLive
          ? <span data-bdd='dialog-access-header-message' className={componentStyles[`heading-text-400`]} >This talk is currently live</span>
          : <Countdown
            date={props.date}
            color='black'
            countdownText='Live in'
            className={componentStyles[`access-header-countdown`]}
          />
      }
      {
        isAdmin && <p className={componentStyles['heading-text-400']}>Click <a className={componentStyles['heading-text-400']} href={`${apiGateway.getBTBasePath()}/talks/control-centre/${roomname}`}>here</a> for Talks Control Centre</p>
      }
    </Modal.Header>
    <Modal.Body className={componentStyles[`Body`]} dataBdd={`dialog-access-body`}>
      <div className={componentStyles[`access-option`]}>
        <div className={componentStyles[`Text`]}>
          <div><Icon name={IconType.iconName.user} /> <div className={componentStyles['access-option-user-type']}>Presenters</div></div>
          <div>Present to your live audience with your webcam and microphone.</div>
          <div>Up to {props.maxPresenters} concurrent presenters supported. Each presenter must use their own BrightTALK account.</div>
        </div>
        <Button
          color={IconType.colour.primary}
          text='Enter as presenter'
          data-bdd='dialog-button-presenter'
          onClick={() => {
            audit.addEntry('PresenterService', 'EnterAsPresenterClicked', JSON.stringify({
              localTimestamp: Date.now().toString(),
              cookies: document.cookie,
              browser: {
                vendor: navigator.vendor,
                userAgent: navigator.userAgent
              }
            }))
            setIsPresenterLoading(true)
            setRole(PRESENTER)

          }}
          className={componentStyles[`Button`]}
          disabled={isDisabled}
          loading={isPresenterLoading}
          block={true}
        />
      </div>
      <div className={componentStyles[`access-option`]}>
        <div className={componentStyles[`Text`]}>
          <div><Icon name={IconType.iconName.eye} /> <div className={componentStyles['access-option-user-type']}>Observers</div></div>
          <div>Observe the presenter screen and assist the presenters with their live talk.</div>
          <div>Up to 10 concurrent observers supported. Access to webcam and microphone is not available to observers.</div>
        </div>
        <Button
          color={IconType.colour.secondary}
          text='Enter as observer'
          data-bdd='dialog-button-observer'
          onClick={() => {
            audit.addEntry('PresenterService', 'EnterAsObserverClicked', JSON.stringify({
              localTimestamp: Date.now().toString(),
              cookies: document.cookie,
              browser: {
                vendor: navigator.vendor,
                userAgent: navigator.userAgent
              }
            }))
            setIsObserverLoading(true)
            setRole(OBSERVER)
            joinRoom()

          }}
          className={componentStyles[`Button`]}
          loading={isObserverLoading}
          block={true}
        />
      </div>
    </Modal.Body>
    <Modal.Footer className={componentStyles[`Footer`]} />
  </Modal>

}

export default AccessModal
